import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  IconFileText,
  IconTable,
  IconWifi,
  IconArrowRight,
  IconDatabase,
  IconListChecks,
} from './ToolsIcons';
import CustomsEntryAnalyzer from '../../analyzers/CustomsEntryAnalyzer';
import AIReportBuilder from '../../reports/AIReportBuilder';
import Library from '../library/Library';
import SOP from '../sop/SOP';
import './importTools.css';

type ToolKey = 'upload' | 'reports' | 'api' | 'library' | 'rules';

type Tool = {
  key: ToolKey;
  name: string;
  blurb: string;
  icon: (props: { className?: string }) => React.JSX.Element;
  disabled?: boolean;
};

// 'hub' is the Import Hub homepage Intelligence · API tab; 'database' is the
// Documents tab on an individual database.
type ImportToolsVariant = 'hub' | 'database';

const TOOLS: Record<ImportToolsVariant, Tool[]> = {
  hub: [
    {
      key: 'reports',
      name: 'Report Generation',
      blurb: 'Build and review generated reports',
      icon: IconTable,
    },
    {
      key: 'library',
      name: 'Report Library',
      blurb: 'Browse every source document and generated output',
      icon: IconDatabase,
    },
    {
      key: 'api',
      name: 'API',
      blurb: 'Connect and pull data programmatically',
      icon: IconWifi,
      disabled: true,
    },
  ],
  database: [
    {
      key: 'upload',
      name: 'Document Upload',
      blurb: 'Bring broker filings and source documents in for analysis',
      icon: IconFileText,
    },
    {
      key: 'library',
      name: 'Library',
      blurb: 'Browse every source document and generated output',
      icon: IconDatabase,
    },
    {
      key: 'rules',
      name: 'Database Rules',
      blurb: 'Define the rules applied to products in this database',
      icon: IconListChecks,
    },
  ],
};

const ImportTools = ({
  variant = 'hub',
  databaseId,
  databaseName,
}: {
  variant?: ImportToolsVariant;
  /** The database whose Documents tab is rendering these tools. */
  databaseId?: string;
  databaseName?: string;
}): React.JSX.Element => {
  const tools = TOOLS[variant];
  const history = useHistory();
  const location = useLocation();
  // The open tool lives in the URL (?tool=rules) so a refresh, or a shared
  // link, reopens it, and Back returns to the tool list.
  const requested = new URLSearchParams(location.search).get('tool');
  const activeTool = tools.find(
    (tool) => tool.key === requested && !tool.disabled,
  );
  const setActive = (key: ToolKey | null) => {
    const params = new URLSearchParams(location.search);
    if (key) params.set('tool', key);
    else params.delete('tool');
    const search = params.toString();
    history.push({
      pathname: location.pathname,
      search: search ? `?${search}` : '',
    });
  };

  if (activeTool) {
    const Icon = activeTool.icon;

    return (
      <div
        className={`iml-root iml-style-island iml-page is-${activeTool.key}`}
      >
        <div className='iml-page-bar'>
          <button
            type='button'
            className='iml-back'
            onClick={() => setActive(null)}
          >
            <IconArrowRight className='iml-back-icon' />
            Back to tools
          </button>
          <div className='iml-page-title'>
            <div className='iml-row-icon'>
              <Icon className='iml-icon' />
            </div>
            {activeTool.name}
          </div>
        </div>

        <div className='iml-page-body'>
          {(activeTool.key === 'upload' || activeTool.key === 'reports') && (
            <div className='iml-embedded-workflow'>
              {activeTool.key === 'upload' && <CustomsEntryAnalyzer embedded />}
              {activeTool.key === 'reports' && <AIReportBuilder embedded />}
            </div>
          )}
          {activeTool.key === 'api' && (
            <div className='iml-panel-placeholder'>
              {activeTool.name} will live here
            </div>
          )}
          {activeTool.key === 'library' && (
            <div className='iml-library-surface'>
              <Library />
            </div>
          )}
          {activeTool.key === 'rules' && (
            <SOP databaseId={databaseId} databaseName={databaseName} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='iml-root iml-style-island'>
      <div className='iml-shelf'>
        {tools.map((tool) => {
          const Icon = tool.icon;

          return (
            <div
              key={tool.key}
              className={`iml-row is-${tool.key}${
                tool.disabled ? ' is-disabled' : ''
              }`}
            >
              <button
                type='button'
                className='iml-row-head'
                disabled={tool.disabled}
                onClick={() => setActive(tool.key)}
              >
                <div className='iml-row-icon'>
                  <Icon className='iml-icon' />
                </div>
                <div className='iml-row-text'>
                  <div className='iml-row-title'>
                    <div className='iml-row-name'>{tool.name}</div>
                    {tool.disabled && (
                      <span className='iml-coming-soon'>COMING SOON</span>
                    )}
                  </div>
                  <div className='iml-row-blurb'>{tool.blurb}</div>
                </div>
                {!tool.disabled && <IconArrowRight className='iml-row-arrow' />}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ImportTools;
